/**
 * Created by shanie on 11/3/16.
 */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';

// Components
import BulkActions from '../Items/BulkActions';
import DropDown from './DropDown';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import SearchBar from './SearchBar';
import SwitchButton from './SwitchButton';
import { Button } from 'dibs-elements/exports/Button';
import ToolbarMobile from './ToolbarMobile';
import ToggleViewButtons from './ToggleViewButtons';

// Helpers
import actions from '../../actions/FavoritesActions';
import { copy, deviceTypes, cutoffs, sortBy, tabs, viewModes } from '../../constants';
import { updateSearchParams } from '../../helpers/urlHelpers';

// Styles
import styles from './styles/Toolbar.scss';

const RightElements = props => {
    return (
        <div className={styles.right}>
            {props.deviceType !== deviceTypes.DESKTOP ? null : (
                <div className={styles.rearrangeSwitch}>
                    <SwitchButton
                        key="switchButton"
                        dataTn="rearrange-items-toggle"
                        onSwitch={props.onSwitch}
                        showTooltip
                        switchOn={props.switchOn}
                        switchLabel={props.switchLabel}
                        tab={props.tab}
                    />
                </div>
            )}
            <div className={styles.dropDown}>
                <DropDown
                    defaultSelection={props.sortBySelection}
                    label={'Sort'}
                    onSelect={props.onSelect}
                    selectOptions={props.selectOptions}
                    size="medium"
                    value={props.sortBySelection}
                />
            </div>
            {props.viewMode && (
                <ToggleViewButtons onClick={props.onToggleView} viewMode={props.viewMode} />
            )}
            {props.tab === tabs.FOLDERS && props.sticky ? (
                <Button
                    className={`${styles.button} favorites`}
                    dataTn="create-folder-button"
                    disabled={props.selecting}
                    onClick={props.onCreateFolder}
                    size="medium"
                >
                    {copy.bulkActions.CREATE_FOLDER}
                </Button>
            ) : null}
        </div>
    );
};

RightElements.propTypes = {
    deviceType: PropTypes.string,
    onCreateFolder: PropTypes.func,
    onSelect: PropTypes.func,
    sortBySelection: PropTypes.string,
    onSwitch: PropTypes.func,
    onToggleView: PropTypes.func,
    selecting: PropTypes.bool,
    selectOptions: PropTypes.array,
    sticky: PropTypes.bool,
    switchLabel: PropTypes.string,
    switchOn: PropTypes.bool,
    tab: PropTypes.string,
    viewMode: PropTypes.string,
};

const ToolbarElement = props => {
    if (props.deviceType === deviceTypes.MOBILE) {
        return (
            <ToolbarMobile
                showSearch={props.tab !== tabs.FOLDERS}
                onSearch={props.onSearch}
                onSelect={props.onSelect}
                searchTerm={props.searchTerm}
                selectOptions={props.selectOptions}
                sortBySelection={props.sortBySelection}
            />
        );
    }
    return (
        <div className={styles.container}>
            {props.sticky ? <div className={styles.title}>{props.title}</div> : null}
            {props.showSearchBar && props.totalItems <= cutoffs.MAX_ITEMS ? (
                <SearchBar onSearch={props.onSearch} searchTerm={props.searchTerm} />
            ) : null}
            {props.totalItems <= cutoffs.MAX_ITEMS ? (
                <RightElements
                    deviceType={props.deviceType}
                    onCreateFolder={props.onCreateFolder}
                    onSelect={props.onSelect}
                    onSwitch={props.onSwitch}
                    onToggleView={props.onToggleView}
                    selectOptions={props.selectOptions}
                    sortBySelection={props.sortBySelection}
                    sticky={props.sticky}
                    switchOn={props.switchOn}
                    switchLabel={
                        props.tab === tabs.FOLDERS ? copy.rearrange.FOLDERS : copy.rearrange.ITEMS
                    }
                    tab={props.tab}
                    viewMode={props.viewMode}
                    value={props.sortBySelection}
                />
            ) : null}
        </div>
    );
};

ToolbarElement.propTypes = {
    deviceType: PropTypes.string,
    onCreateFolder: PropTypes.func,
    onSearch: PropTypes.func,
    onSelect: PropTypes.func,
    searchTerm: PropTypes.string,
    showSearchBar: PropTypes.bool,
    sortBySelection: PropTypes.string,
    onSwitch: PropTypes.func,
    onToggleView: PropTypes.func,
    selectOptions: PropTypes.array,
    sticky: PropTypes.bool,
    switchOn: PropTypes.bool,
    tab: PropTypes.string,
    title: PropTypes.string,
    totalItems: PropTypes.number,
    viewMode: PropTypes.string,
};

class Toolbar extends React.Component {
    constructor(props) {
        super(props);

        this.enableAnimation = false;
        this.location = 0;
        this.ticking = false;

        this.applySearchTerm = this.applySearchTerm.bind(this);
        this.applySort = this.applySort.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.toggleReorderMode = this.toggleReorderMode.bind(this);
    }

    componentDidMount() {
        this.enableAnimation = true;
        if (this.props.deviceType !== deviceTypes.MOBILE) {
            window.addEventListener('scroll', this.handleScroll);
        }
        if (this.props.deviceType === deviceTypes.DESKTOP) {
            window.addEventListener('resize', this.handleResize);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
        if (this.props.deviceType === deviceTypes.DESKTOP) {
            this.props.setViewMode(viewModes.GRID);
        }
    }

    handleScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                const position = window.pageYOffset;
                if (!this.props.sticky) {
                    // get location of toolbar relative to the elements above it.
                    let location = 0;
                    let toolbar = ReactDOM.findDOMNode(this);
                    if (toolbar.offsetParent) {
                        do {
                            location += toolbar.offsetTop;
                            toolbar = toolbar.offsetParent;
                        } while (toolbar);
                    }
                    this.location = location;
                }
                // If user's current position on page surpasses position of toolbar when non-sticky, stick the toolbar,
                // otherwise, un-stick the toolbar
                if (position > this.location) {
                    this.props.setStickToolbar(true);
                } else {
                    this.props.setStickToolbar(false);
                }
                this.ticking = false;
            });
        }
        this.ticking = true;
    }

    applySearchTerm(searchTerm) {
        if (searchTerm === '') {
            this.props.resetUiState();
        } else {
            this.props.resetUiState({ retainItems: false, retainSort: true });
        }

        updateSearchParams('search', searchTerm);
        this.props.setSearchTerm(searchTerm);
    }

    applySort(sortBySelection) {
        this.props.resetUiState({ retainItems: false, retainSort: true, retainSearch: true });
        this.props.setSortBy(sortBySelection);
        updateSearchParams('sort', sortBySelection);
    }

    toggleReorderMode() {
        this.props.resetUiState();
        this.props.setSortBy(sortBy.CUSTOM);
        this.props.setReorderMode(!this.props.reordering);
    }

    render() {
        const {
            relay,
            deviceType,
            portfolioId,
            removeItems,
            reordering,
            searchTerm,
            selectedItemIds,
            selectedItems,
            selecting,
            selectOptions,
            shareItems,
            showSearchBar,
            sortBySelection,
            sticky,
            tab,
            totalItems,
            userId,
            viewer,
            viewMode,
        } = this.props;

        let toolBarContent = (
            <TransitionGroup>
                <CSSTransition
                    classNames={styles}
                    timeout={{ exit: 500, enter: 500 }}
                    appear={this.enableAnimation}
                >
                    <ToolbarElement
                        key="toolbar"
                        deviceType={deviceType}
                        searchTerm={searchTerm}
                        selectOptions={selectOptions}
                        onCreateFolder={this.props.onCreateFolder}
                        onSearch={this.applySearchTerm}
                        onSelect={this.applySort}
                        onSwitch={this.toggleReorderMode}
                        onToggleView={this.props.setViewMode}
                        showSearchBar={showSearchBar}
                        sortBySelection={sortBySelection}
                        sticky={sticky}
                        switchOn={reordering}
                        tab={tab}
                        title={this.props.title}
                        totalItems={totalItems}
                        viewMode={viewMode}
                    />
                </CSSTransition>
            </TransitionGroup>
        );

        if (selecting) {
            toolBarContent = (
                <div className={`rowFlex ${styles.bulkActions}`}>
                    <BulkActions
                        relay={relay}
                        portfolioId={portfolioId}
                        isTablet={deviceType === deviceTypes.TABLET}
                        removeItems={removeItems}
                        selectedItemIds={selectedItemIds}
                        selectedItems={selectedItems}
                        shareItems={shareItems}
                        sticky={sticky}
                        totalItems={totalItems}
                        userId={userId}
                        viewer={viewer}
                    />
                </div>
            );
        }

        return <div>{toolBarContent}</div>;
    }
}

Toolbar.defaultProps = {
    selectOptions: [],
    showSearchBar: false,
};

Toolbar.propTypes = {
    relay: PropTypes.object,
    deviceType: PropTypes.string,
    isTablet: PropTypes.bool,
    onCreateFolder: PropTypes.func,
    openModalType: PropTypes.string,
    portfolioId: PropTypes.string,
    removeItems: PropTypes.func,
    reordering: PropTypes.bool,
    resetUiState: PropTypes.func,
    searchTerm: PropTypes.string,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    selectOptions: PropTypes.array,
    setReorderMode: PropTypes.func,
    setSearchTerm: PropTypes.func,
    setSortBy: PropTypes.func,
    setViewMode: PropTypes.func,
    setStickToolbar: PropTypes.func,
    shareItems: PropTypes.func,
    showSearchBar: PropTypes.bool,
    sortBySelection: PropTypes.string,
    sticky: PropTypes.bool,
    tab: PropTypes.string,
    title: PropTypes.string,
    totalItems: PropTypes.number,
    viewMode: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    const tab = state.favorites.uiState.tab;
    return {
        deviceType: state.favorites.uiState.deviceType,
        reordering: state.favorites.uiState.reordering,
        searchTerm: state.favorites.uiState.searchTerm,
        selecting: state.favorites.uiState.selecting,
        sortBySelection: state.favorites.uiState[`${tab}Sort`],
        sticky: state.favorites.uiState.stickToolbar,
        tab,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: ({ retainItems = false, retainSort = false, retainSearch = false } = {}) => {
            dispatch(actions.resetUiState({ retainItems, retainSort, retainSearch }));
        },
        setReorderMode: reordering => {
            dispatch(actions.setReorderMode({ reordering }));
        },
        setSearchTerm: searchTerm => {
            dispatch(actions.setSearchTerm({ searchTerm }));
        },
        setSortBy: sortBySelection => {
            dispatch(actions.setSortBy({ sortBySelection }));
        },
        setStickToolbar: stickToolbar => {
            dispatch(actions.setStickToolbar({ stickToolbar }));
        },
        setViewMode: viewMode => {
            dispatch(actions.setViewMode({ viewMode }));
        },
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Toolbar);
